import { initPostHog, shutdownPostHog } from '@/lib/posthog/posthog-init';
import { initGTM, shutdownGTM } from '@/lib/gtm/gtm-init';
import { store } from '@/redux/store';
import { identifyUser } from '@/lib/posthog/posthog-user';
import { sendMetaPageView } from '@/lib/meta/meta-pageview';
import { scrubPii } from '@/lib/scrub-pii';
import {
  hasMarketingConsent,
  readAcceptedCookies
} from '@/lib/consent/marketing-consent';

export { hasMarketingConsent };

// CCM19 accepted cookie/service names to check for analytics consent.
// The exact category name configured in the CCM19 admin panel is:
// "Analyse / Statistiken"
// Additional variants are kept as fallbacks.
const ANALYTICS_COOKIES = [
  // Exact CCM19 category name as configured
  'Analyse / Statistiken',
  // English category names (fallback)
  'statistics',
  'analytics',
  // Actual browser cookie names (fallback)
  '_ga',
  '_gid',
  // PostHog cookies (wildcard pattern)
  'ph_*'
];

declare global {
  interface Window {
    CCM?: {
      acceptedCookies?: string[];
      openWidget?: () => void;
      closeWidget?: () => void;
    };
  }
}

let pollInterval: NodeJS.Timeout | null = null;
let lastConsentState = false;
let entryPageViewSent = false;
const MAX_CONSENT_POLL_ATTEMPTS = 60; // 60 * 500ms = 30 seconds max
let pollAttempts = 0;

// Max attempts for checkCCMReady: 50 * 100ms = 5 seconds
const MAX_CCM_READY_ATTEMPTS = 50;
let ccmReadyAttempts = 0;

// Track pending timeouts so they can be cancelled on cleanup
const pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

const trackTimeout = (id: ReturnType<typeof setTimeout>) => {
  pendingTimeouts.push(id);
  return id;
};

const clearAllTimeouts = () => {
  for (const id of pendingTimeouts) {
    clearTimeout(id);
  }
  pendingTimeouts.length = 0;
};

/**
 * Reset all module-level mutable state.
 * Called during cleanup to prevent stale state across React remounts.
 */
const resetState = () => {
  lastConsentState = false;
  pollAttempts = 0;
  ccmReadyAttempts = 0;
  clearAllTimeouts();
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
};

/**
 * Setup consent listeners for CCM19
 * Initializes analytics (PostHog + GA4) only after consent is granted
 */
export const setupConsentListener = () => {
  if (typeof window === 'undefined') return;

  // Reset all state so remounts start clean
  resetState();

  // Listen for CCM19 consent accept event
  window.addEventListener(
    'CCMConsentAccepted',
    handleConsentAccepted as EventListener
  );

  // Listen for CCM19 consent reject/withdrawal event
  window.addEventListener(
    'CCMConsentRejected',
    handleConsentRejected as EventListener
  );

  // Check if consent was already given on page load
  // Wait for CCM to be ready (it loads asynchronously)
  const checkCCMReady = () => {
    if (window.CCM) {
      checkInitialConsent();
      startConsentPolling();
    } else if (ccmReadyAttempts < MAX_CCM_READY_ATTEMPTS) {
      ccmReadyAttempts++;
      trackTimeout(setTimeout(checkCCMReady, 100));
    }
    // else: CCM19 never loaded (ad blocker / network failure) — silently stop
  };

  // Start checking after a brief delay to allow CCM19 script to load
  trackTimeout(setTimeout(checkCCMReady, 500));
};

/**
 * Poll CCM consent state as fallback if events don't fire
 */
const startConsentPolling = () => {
  if (pollInterval) return;

  pollAttempts = 0;

  pollInterval = setInterval(() => {
    pollAttempts += 1;
    if (pollAttempts >= MAX_CONSENT_POLL_ATTEMPTS) {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
      return;
    }

    if (!window.CCM) return;

    const acceptedCookies = readAcceptedCookies();
    const hasConsent = hasAnalyticsConsent(acceptedCookies);

    if (hasConsent && !lastConsentState) {
      startTracking();
    }

    if (!hasConsent && lastConsentState) {
      stopTracking();
    }

    lastConsentState = hasConsent;

    sendEntryPageView();
  }, 500);
};

/**
 * Check if analytics cookies are accepted.
 * Case-insensitive to handle CCM19 returning German or English category names.
 * Also matches partial prefixes for wildcard patterns (e.g. ph_*).
 */
const hasAnalyticsConsent = (acceptedCookies: string[]): boolean => {
  if (!acceptedCookies.length) return false;

  return ANALYTICS_COOKIES.some((cookieName) => {
    if (cookieName.includes('*')) {
      const prefix = cookieName.replace('*', '').toLowerCase();
      return acceptedCookies.some((cookie) =>
        cookie.toLowerCase().startsWith(prefix)
      );
    }
    return acceptedCookies.some(
      (cookie) => cookie.toLowerCase() === cookieName.toLowerCase()
    );
  });
};

/**
 * Re-identify the current user in PostHog if they are already logged in
 * Handles the case where consent is granted after login.
 */
const reIdentifyIfLoggedIn = () => {
  const analytics = store.getState().auth.analytics;
  if (analytics?.analytics_id) {
    identifyUser(
      analytics.analytics_id,
      analytics.user_segment,
      analytics.verified_status,
      analytics.cio_marketable
    );
  }
};

const startTracking = () => {
  initGTM();
  initPostHog();
  reIdentifyIfLoggedIn();
};

const stopTracking = () => {
  shutdownPostHog();
  shutdownGTM();
  window.location.reload();
};

const sendEntryPageView = () => {
  if (entryPageViewSent || !hasMarketingConsent()) return;
  entryPageViewSent = true;
  sendMetaPageView(
    `${window.location.origin}${scrubPii(window.location.pathname + window.location.search)}`
  );
};

export const markEntryPageViewSent = () => {
  entryPageViewSent = true;
};

/**
 * Check if user has already given consent (on page load)
 */
const checkInitialConsent = () => {
  if (typeof window === 'undefined' || !window.CCM) return;

  const acceptedCookies = readAcceptedCookies();

  if (hasAnalyticsConsent(acceptedCookies)) {
    startTracking();
    lastConsentState = true;
  }

  sendEntryPageView();
};

/**
 * Handle CCM19 consent accepted event.
 * CCM19 only dispatches this event when the user explicitly accepts cookies.
 * When the event payload includes an explicit cookie list, check it for
 * analytics consent. When the payload is empty, fall back to the CCM global
 * state. Never start tracking without a positive analytics-consent match.
 */
const handleConsentAccepted = (event: CustomEvent) => {
  const eventCookies: string[] = event.detail?.acceptedCookies || [];

  // Prefer the event payload; fall back to the CCM global state when the
  // payload is empty (some CCM19 configurations omit the cookie list).
  const cookiesToCheck =
    eventCookies.length > 0 ? eventCookies : readAcceptedCookies();

  if (hasAnalyticsConsent(cookiesToCheck) && !lastConsentState) {
    lastConsentState = true;
    startTracking();
  }

  sendEntryPageView();
};

/**
 * Handle CCM19 consent rejected/withdrawn event
 * Always shut down analytics — do not rely on event payload structure
 */
const handleConsentRejected = (_event: CustomEvent) => {
  if (!lastConsentState) return;

  lastConsentState = false;
  stopTracking();
};

/**
 * Cleanup consent listeners
 */
export const cleanupConsentListener = () => {
  if (typeof window === 'undefined') return;

  window.removeEventListener(
    'CCMConsentAccepted',
    handleConsentAccepted as EventListener
  );
  window.removeEventListener(
    'CCMConsentRejected',
    handleConsentRejected as EventListener
  );

  resetState();
};
